import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { BOARD_Z_INDEX, formatEstimatedHours } from "../../../app/utils/projectBoard/boardDesignTokens";
import { BoardButton, BoardIconButton } from "../../atoms/BoardDesignSystem";
import "./BoardActualHoursModal.scss";

const BoardActualHoursModal = ({
  open,
  cardTitle,
  estimatedHours = null,
  onClose,
  onConfirm,
  loading = false,
}) => {
  const [actualHours, setActualHours] = useState("");
  const [overReason, setOverReason] = useState("");
  const [error, setError] = useState("");

  const estimatedValue = useMemo(() => {
    if (estimatedHours == null || estimatedHours === "") return null;
    const parsed = Number(estimatedHours);
    return Number.isNaN(parsed) ? null : parsed;
  }, [estimatedHours]);

  const actualValue = useMemo(() => {
    const trimmed = actualHours.trim();
    if (trimmed === "") return null;
    const parsed = Number(trimmed);
    return Number.isNaN(parsed) ? null : parsed;
  }, [actualHours]);

  const requiresOverReason = estimatedValue != null
    && actualValue != null
    && actualValue > estimatedValue;

  useEffect(() => {
    if (!open) return;
    setActualHours("");
    setOverReason("");
    setError("");
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !loading) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, loading, onClose]);

  if (!open) return null;

  const handleConfirm = async () => {
    const trimmed = actualHours.trim();
    const parsed = trimmed === "" ? NaN : Number(trimmed);
    if (trimmed === "" || Number.isNaN(parsed) || parsed < 0) {
      setError("Enter a valid non-negative number of hours.");
      return;
    }

    if (estimatedValue != null && parsed > estimatedValue && !overReason.trim()) {
      setError("Please explain why the task took longer than estimated.");
      return;
    }

    setError("");
    const needsOverReason = estimatedValue != null && parsed > estimatedValue;
    await onConfirm?.({
      actualHours: parsed,
      actualHoursOverReason: needsOverReason ? overReason.trim() : "",
    });
  };

  return createPortal(
    <div
      className="pb-modal__overlay"
      onClick={() => !loading && onClose?.()}
      role="presentation"
    >
      <div
        className="pb-modal__dialog pb-actual-hours-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="board-actual-hours-title"
        onClick={(e) => e.stopPropagation()}
        style={{ zIndex: BOARD_Z_INDEX.modal + 5 }}
      >
        <div className="pb-actual-hours-modal__header">
          <h2 id="board-actual-hours-title" className="pb-actual-hours-modal__title">
            Actual time spent
          </h2>
          <BoardIconButton icon="✕" label="Close" onClick={onClose} disabled={loading} />
        </div>

        <p className="pb-actual-hours-modal__subtitle">
          Moving <strong>{cardTitle || "this card"}</strong> to Dev QA Done.
          How many hours did this task take?
        </p>

        {estimatedValue != null && (
          <div className="pb-actual-hours-modal__estimate">
            <span className="pb-actual-hours-modal__estimate-label">Estimated hours</span>
            <span className="pb-actual-hours-modal__estimate-value">
              {formatEstimatedHours(estimatedValue)}
            </span>
          </div>
        )}

        <div className="pb-actual-hours-modal__field">
          <label className="pb-actual-hours-modal__label" htmlFor="board-actual-hours-input">
            Actual hours
          </label>
          <div className="pb-actual-hours-modal__input-wrap">
            <input
              id="board-actual-hours-input"
              type="number"
              className="pb-actual-hours-modal__input"
              min="0"
              step="0.25"
              value={actualHours}
              onChange={(e) => {
                setActualHours(e.target.value);
                if (error) setError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !requiresOverReason) handleConfirm();
              }}
              placeholder="e.g. 6"
              disabled={loading}
              autoFocus
            />
            <span className="pb-actual-hours-modal__suffix">hrs</span>
          </div>
        </div>

        {requiresOverReason && (
          <div className="pb-actual-hours-modal__field">
            <label className="pb-actual-hours-modal__label" htmlFor="board-actual-hours-reason">
              Why did it take longer?
            </label>
            <textarea
              id="board-actual-hours-reason"
              className="pb-actual-hours-modal__reason"
              rows={3}
              value={overReason}
              onChange={(e) => {
                setOverReason(e.target.value);
                if (error) setError("");
              }}
              placeholder="Explain why actual time exceeded the estimate..."
              disabled={loading}
            />
          </div>
        )}

        {error && <p className="pb-actual-hours-modal__error">{error}</p>}

        <div className="pb-actual-hours-modal__actions">
          <BoardButton variant="ghost-dark" size="sm" onClick={onClose} disabled={loading}>
            Cancel
          </BoardButton>
          <BoardButton variant="primary" size="sm" onClick={handleConfirm} disabled={loading}>
            {loading ? "Moving..." : "Move card"}
          </BoardButton>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BoardActualHoursModal;
